import { useEffect, useId, useState } from 'react';
import { ENV_NAME_RE, PROJECT_KEY_RE, type VpsAllowanceDTO, type VpsDTO } from '@devrobot/shared';
import { Plus, X } from 'lucide-react';
import { api, seg } from '../../api/client';
import { useApiMutation, useProjects } from '../../api/queries';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';

const rowValid = (a: VpsAllowanceDTO) => (a.projectKey === '*' || PROJECT_KEY_RE.test(a.projectKey)) && (a.environment === '*' || ENV_NAME_RE.test(a.environment));

/** Which project × environment combinations may deploy to this VPS (platform configuration). */
export function AllowancesEditor({ vps, editable }: { vps: VpsDTO; editable: boolean }) {
  const toast = useToast();
  const id = useId();
  const projects = useProjects();
  const [rows, setRows] = useState<VpsAllowanceDTO[]>(vps.allowances);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) setRows(vps.allowances);
  }, [vps.allowances, dirty]);

  const save = useApiMutation((allowances: VpsAllowanceDTO[]) => api.put<VpsDTO>(`/api/v1/vps/${seg(vps.id)}/allowances`, { allowances }), { invalidate: [['vps']] });
  const update = (i: number, patch: Partial<VpsAllowanceDTO>) => {
    setDirty(true);
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const valid = rows.every(rowValid);

  return (
    <div className="space-y-3">
      <div className="space-y-1 text-sm text-base-content/65">
        <p>A deployment to this VPS is allowed when one row matches both its project and its environment.</p>
        <p>
          New servers allow every project’s <strong>development</strong> and <strong>staging</strong>. Production is not included by default: add an explicit row (for example <code className="font-mono">shop × production</code>). A{' '}
          <code className="font-mono">*</code> environment matches every environment, production included.
        </p>
      </div>
      {!editable ? (
        <ul className="flex flex-wrap gap-1.5">
          {vps.allowances.length === 0 ? <li className="text-sm text-base-content/60">No project may deploy here.</li> : null}
          {vps.allowances.map((a, i) => (
            <li key={i} className="badge badge-ghost font-mono">
              {a.projectKey} × {a.environment}
            </li>
          ))}
        </ul>
      ) : (
        <>
          <datalist id={`${id}-projects`}>
            <option value="*" />
            {(projects.data ?? []).map((p) => (
              <option key={p.key} value={p.key} />
            ))}
          </datalist>
          <datalist id={`${id}-envs`}>
            {['*', 'development', 'staging', 'production'].map((e) => (
              <option key={e} value={e} />
            ))}
          </datalist>
          {rows.length === 0 ? <p className="animate-rise text-sm dr-ink-warning">No rows: no project may deploy to this server.</p> : null}
          {rows.map((r, i) => (
            <div key={i} className="flex animate-rise items-center gap-2 rounded-field bg-base-200/60 p-1.5">
              <input
                className={`input input-sm w-full font-mono ${rowValid(r) || !r.projectKey ? '' : 'input-error'}`}
                list={`${id}-projects`}
                value={r.projectKey}
                onChange={(e) => update(i, { projectKey: e.target.value.trim() })}
                aria-label={`Project for row ${i + 1}`}
                placeholder="project key or *"
              />
              <span aria-hidden="true" className="text-base-content/50">
                ×
              </span>
              <input
                className={`input input-sm w-full font-mono ${r.environment === '*' || ENV_NAME_RE.test(r.environment) || !r.environment ? '' : 'input-error'}`}
                list={`${id}-envs`}
                value={r.environment}
                onChange={(e) => update(i, { environment: e.target.value.trim() })}
                aria-label={`Environment for row ${i + 1}`}
                placeholder="environment or *"
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-square"
                onClick={() => {
                  setDirty(true);
                  setRows(rows.filter((_, j) => j !== i));
                }}
                aria-label={`Remove row ${i + 1}`}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setDirty(true);
                setRows([...rows, { projectKey: '*', environment: 'development' }]);
              }}
            >
              <Plus className="size-4" aria-hidden="true" /> Add row
            </button>
            <div className="flex gap-2">
              {dirty ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setRows(vps.allowances);
                    setDirty(false);
                    save.reset();
                  }}
                >
                  Discard
                </button>
              ) : null}
              <Haptic disabled={!dirty || !valid || save.isPending}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!dirty || !valid || save.isPending}
                  onClick={() =>
                    save.mutate(rows, {
                      onSuccess: () => {
                        setDirty(false);
                        toast.success('Allowances saved', { description: 'Existing deployments are not affected.' });
                      },
                      onError: () => haptic('error'),
                    })
                  }
                >
                  {save.isPending ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : null}
                  Save allowances
                </button>
              </Haptic>
            </div>
          </div>
          <ErrorAlert error={save.error} title="Allowances were not saved" />
        </>
      )}
    </div>
  );
}
